/**
 * TeamLeavePanel — combined view of teammates currently on approved leave
 * (top section) and approved leave starting in the next 30 days (bottom).
 * Reuses StaffOnLeaveModelView for both endpoints; for upcoming rows the
 * server packs "days until start" into daysRemaining.
 */

import { useQuery } from '@tanstack/react-query';
import {
  Avatar,
  Badge,
  Card,
  Divider,
  Group,
  Skeleton,
  Stack,
  Text,
  ThemeIcon,
  Title,
} from '@/components/ui';
import { IconUmbrella } from '@tabler/icons-react';
import {
  useStaffPortalCalendarService,
  type StaffOnLeaveModelView,
} from '@actschurch/ccms-sdk';
import { formatDate } from '../../utils/hrHelpers';
import { useEmployeeQuickView } from '../../components';

export function TeamLeavePanel() {
  const calendar = useStaffPortalCalendarService();
  const openEmployee = useEmployeeQuickView();

  const { data: onLeave = [], isLoading: onLeaveLoading } = useQuery({
    queryKey: ['hr', 'staff', 'dashboard', 'team-leave', 'on-leave'],
    queryFn: () => calendar.getOnLeave(),
  });
  const { data: upcoming = [], isLoading: upcomingLoading } = useQuery({
    queryKey: ['hr', 'staff', 'dashboard', 'team-leave', 'upcoming'],
    queryFn: () => calendar.getUpcomingLeave(30),
  });

  const onLeaveList = onLeave as StaffOnLeaveModelView[];
  const upcomingList = upcoming as StaffOnLeaveModelView[];
  const isLoading = onLeaveLoading || upcomingLoading;

  const isEmpty = !isLoading && onLeaveList.length === 0 && upcomingList.length === 0;

  return (
    <Card withBorder padding="lg" radius="md" mih={250} style={{ display: 'flex', flexDirection: 'column' }}>
      <Group gap="xs" mb="sm">
        <ThemeIcon size="md" variant="light" color="cyan" radius="xl">
          <IconUmbrella size={16} />
        </ThemeIcon>
        <Title order={5}>Team Leave</Title>
      </Group>

      {isLoading ? (
        <Stack gap="xs">
          <Skeleton height={28} />
          <Skeleton height={28} />
          <Skeleton height={28} />
        </Stack>
      ) : isEmpty ? (
        <Stack flex={1} justify="center" align="center">
          <Text size="md" c="dimmed" ta="center">
            No one is on leave right now.
          </Text>
        </Stack>
      ) : (
        <Stack gap="md">
          {onLeaveList.length > 0 && (
            <Stack gap="xs">
              <Text size="xs" c="dimmed" tt="uppercase">On leave today</Text>
              {onLeaveList.map((p) => (
                <Group
                  key={`on-${p.userId}-${p.leaveID}`}
                  wrap="nowrap"
                  gap="sm"
                  style={{ cursor: 'pointer' }}
                  onClick={() => openEmployee(p.userId)}
                >
                  <Group gap="sm" wrap="nowrap" style={{ minWidth: 0, flex: 1 }}>
                    <Avatar src={p.avatar ?? undefined} size={32} radius="xl" color="cyan">
                      {(p.firstName ?? '?')[0]}{p.lastInitial}
                    </Avatar>
                    <Text size="sm" fw={500} truncate="end">
                      {p.firstName} {p.lastInitial}.
                    </Text>
                  </Group>
                  <Group gap="xs" wrap="nowrap" style={{ flexShrink: 0 }}>
                    <Text size="xs" c="dimmed">Returns {formatDate(p.endDate)}</Text>
                    <Badge
                      size="sm"
                      variant="light"
                      color={p.leaveTypeColor ? undefined : 'cyan'}
                      style={p.leaveTypeColor ? { backgroundColor: p.leaveTypeColor, color: '#fff' } : undefined}
                    >
                      {p.leaveTypeName}
                    </Badge>
                  </Group>
                </Group>
              ))}
            </Stack>
          )}

          {onLeaveList.length > 0 && upcomingList.length > 0 && <Divider />}

          {upcomingList.length > 0 && (
            <Stack gap="xs">
              <Text size="xs" c="dimmed" tt="uppercase">Upcoming</Text>
              {upcomingList.map((p) => (
                <Group
                  key={`up-${p.userId}-${p.leaveID}`}
                  wrap="nowrap"
                  gap="sm"
                  style={{ cursor: 'pointer' }}
                  onClick={() => openEmployee(p.userId)}
                >
                  <Group gap="sm" wrap="nowrap" style={{ minWidth: 0, flex: 1 }}>
                    <Avatar src={p.avatar ?? undefined} size={32} radius="xl" color="cyan">
                      {(p.firstName ?? '?')[0]}{p.lastInitial}
                    </Avatar>
                    <Text size="sm" fw={500} truncate="end">
                      {p.firstName} {p.lastInitial}.
                    </Text>
                  </Group>
                  <Group gap="xs" wrap="nowrap" style={{ flexShrink: 0 }}>
                    <Text size="xs" c="dimmed">{formatDate(p.startDate)}</Text>
                    <Badge size="sm" variant="light" color="cyan">
                      in {p.daysRemaining}d
                    </Badge>
                  </Group>
                </Group>
              ))}
            </Stack>
          )}
        </Stack>
      )}
    </Card>
  );
}
